/**
 * A page location's discounts: percent off per day of the week, keyed
 * "mon".."sun" (1-90) like the server's user_pages.discounts. The stamp on
 * the page shows today's by the device's day; the edit form lists the seven
 * days with short weekday names in the UI language.
 */

export const DISCOUNT_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export const MAX_DISCOUNT_PERCENT = 90

export type DiscountDay = (typeof DISCOUNT_DAYS)[number]

/** Today's key by the device clock. */
export function todayDiscountKey(): DiscountDay {
  // getDay: Sunday = 0 .. Saturday = 6; the list starts on Monday.
  return DISCOUNT_DAYS[(new Date().getDay() + 6) % 7]
}

/** Today's discount, or null when the page offers none today. */
export function todayDiscount(discounts: Record<string, number> | null | undefined): number | null {
  const percent = discounts?.[todayDiscountKey()]
  return typeof percent === 'number' && percent > 0 ? Math.round(percent) : null
}

/** The short weekday name ("Mon") of a key in the given language. */
export function discountDayName(day: DiscountDay, language: string): string {
  // 2024-01-01 is a Monday.
  const date = new Date(2024, 0, 1 + DISCOUNT_DAYS.indexOf(day))
  try {
    return new Intl.DateTimeFormat(language, { weekday: 'short' }).format(date)
  } catch {
    return day
  }
}

/** Same percentages, whatever the key order. */
export function sameDiscounts(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) if ((a[key] ?? 0) !== (b[key] ?? 0)) return false
  return true
}
